import React, {Component, ReactNode} from 'react';
import Breadcrumb from '../components/Breadcrumb';
import {FormicaProps, withFormica} from '../helpers/withFormica';
import {dispatch} from '../redux/store';
import LanguageUtil from '../util/LanguageUtil';
import MessageUtil from '../util/MessageUtil';
import ServiceContainer from '../config/ServiceContainer';
import {ServiceNames} from '../constant/ServiceNames';
import CustomListService from '../service/CustomListService';
import WatchlistService from '../service/WatchlistService';
import {
  CustomListModel,
  CustomListRecordEntry,
  CustomListRecordItem,
  CustomListTabType,
  CustomListWatchlistName,
  CustomListWatchlistRecord,
} from '../domain/custom-list/CustomListDomains';
import CustomLoading from '../components/custom/CustomLoading';
import CustomEmptyComponent from '../components/custom/CustomEmptyComponent';
import {changeActivePageIndex, changePagePath} from '../redux/actions/PageActions';
import CustomListRouteUtil from '../util/CustomListRouteUtil';
import WatchlistInformationComponent from '../components/kyc/watchlist/WatchlistInformationComponent';
import WatchlistNewsComponent from '../components/kyc/watchlist/WatchlistNewsComponent';
import CustomTabv2, {CustomTabItem} from '../components/custom/CustomTabv2';
import {
  WatchlistIndividualRecord,
  WatchlistListsModel,
  WatchlistModel,
  WatchlistNameType,
} from '../domain/watchlist/WatchlistDomains';
import {WatchlistType} from '../constant/Constants';
import moment from 'moment';
import _ from 'lodash';
import {ConfirmPopup} from 'primereact/confirmpopup';

class State {
  public list: CustomListModel | undefined = undefined;
  public record: CustomListRecordItem | undefined = undefined;
  public watchlistLists: WatchlistListsModel[] = [];
  public listRecordCount: number = 0;
  public loading: boolean = true;
  public loadFailed: boolean = false;
  public date: string = moment().format('DD/MM/yyyy HH:mm');
}

class CustomListRecordDetailContainer extends Component<FormicaProps, State> {
  public state: State = new State();

  private readonly customListService: CustomListService = ServiceContainer.getInstance().get(ServiceNames.CUSTOM_LIST);
  private readonly watchlistService: WatchlistService = ServiceContainer.getInstance().get(ServiceNames.WATCHLIST);

  public render(): ReactNode {
    const {record, loading, loadFailed, date} = this.state;
    const recordType: CustomListTabType = CustomListRouteUtil.resolveTab(this.props.router.params.recordType);
    const watchlistType: WatchlistType = recordType === 'entity' ? 'Entity' : 'Individual';

    if (loading) {
      return (
        <div className={'w-full h-full flex flex-col'}>
          <Breadcrumb/>
          <CustomLoading/>
        </div>
      );
    }

    if (loadFailed || record == undefined) {
      return (
        <div className={'w-full h-full flex flex-col'}>
          <Breadcrumb/>
          <div className={'flex w-full h-full items-center justify-center'}>
            <CustomEmptyComponent
              label={LanguageUtil.get({
                id: 'pages.customList.recordDetailLoadFailed',
                defaultMessage: 'Failed to load record details',
              })}
            />
          </div>
        </div>
      );
    }

    const recordName: string = this.getName(record);

    return (
      <div className={'w-full h-full flex flex-col'}>
        <Breadcrumb/>
        <ConfirmPopup/>
        <div className={'p-5'}>
          <div className={'flex justify-between'}>
            <div className={'flex'}>
              <span
                className="w-[60px] h-[60px] min-h-[60px] min-w-[60px] 2xl:w-[80px] 2xl:h-[80px] 2xl:min-h-[80px] 2xl:min-w-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE]">
                {this.getImage(record, recordType)}
              </span>
              <div className={'flex flex-col justify-center pl-3'}>
                <span className={'text-[24px] 2xl:text-4xl font-normal'}>{recordName}</span>
                <div className={'flex items-center gap-x-2'}>
                  {this.getCategory(record)?.map((value: string, index: number) => (
                    <span
                      key={index}
                      className={'flex min-w-[60px] text-xs shrink-0 2xl:text-md py-1 justify-center px-[8px] items-center rounded-[50px] bg-[#F6F6F9] text-[#4F4F4F]'}
                    >
                      {value}
                    </span>
                  ))}
                  <span
                    className={'flex min-w-[60px] text-xs shrink-0 2xl:text-md py-1 justify-center items-center px-[8px] rounded-[50px] bg-[#F6F6F9] text-[#4F4F4F]'}
                  >
                    {date}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={'flex w-full h-full'}>
          <CustomTabv2 tab={this.getTabs(record, watchlistType, recordName)}/>
        </div>
      </div>
    );
  }

  public async componentDidMount(): Promise<void> {
    await this.loadData();
  }

  public async componentDidUpdate(prevProps: FormicaProps): Promise<void> {
    const prevListId: string | undefined = prevProps.router.params.listId;
    const prevRecordId: string | undefined = prevProps.router.params.recordId;
    const listId: string | undefined = this.props.router.params.listId;
    const recordId: string | undefined = this.props.router.params.recordId;
    if (prevListId !== listId || prevRecordId !== recordId) {
      await this.loadData();
    }
  }

  private getTabs = (
    record: CustomListRecordItem,
    watchlistType: WatchlistType,
    recordName: string,
  ): CustomTabItem[] => {
    const {watchlistLists} = this.state;
    return [
      {
        key: 'Information',
        label: LanguageUtil.get({id: 'pages.watchlist.watchlistDetail.table.information', defaultMessage: 'Information'}),
        content: (
          <WatchlistInformationComponent
            selectedResponse={this.mapToWatchlistModels(record.records)}
            watchlistResponse={watchlistLists}
            type={watchlistType}
            preferredCustomListSource={this.getPreferredCustomListSource()}
          />
        ),
      },
      {
        key: 'News',
        label: LanguageUtil.get({id: 'pages.watchlist.watchlistDetail.table.news', defaultMessage: 'News'}),
        content: <WatchlistNewsComponent name={recordName}/>,
      },
    ];
  };

  private getPreferredCustomListSource = (): {name: string; size: number} | undefined => {
    const {list, listRecordCount} = this.state;
    if (list == undefined) {
      return undefined;
    }
    return {name: list.name, size: listRecordCount};
  };

  private loadData = async (): Promise<void> => {
    const listId: string | undefined = this.props.router.params.listId;
    const recordId: string | undefined = this.props.router.params.recordId;
    if (!listId || !recordId) {
      return;
    }

    this.setState({loading: true, loadFailed: false, record: undefined});
    try {
      const [list, record, watchlistLists, listRecordCount]: [
        CustomListModel,
        CustomListRecordItem,
        WatchlistListsModel[],
        number,
      ] = await Promise.all([
        this.customListService.getById(listId),
        this.customListService.getRecordById(listId, recordId),
        this.watchlistService.getLists(),
        this.customListService.getRecordCount(listId),
      ]);
      this.setState({
        list,
        record,
        watchlistLists: watchlistLists || [],
        listRecordCount,
        loading: false,
        loadFailed: false,
        date: moment().format('DD/MM/yyyy HH:mm'),
      });
      this.setBreadcrumb(list.name, this.getName(record));
    } catch (e) {
      console.error(e);
      this.setState({loading: false, loadFailed: true, record: undefined});
      MessageUtil.error({
        summary: LanguageUtil.get({
          id: 'pages.customList.recordDetailLoadFailed',
          defaultMessage: 'Failed to load record details',
        }),
      });
    }
  };

  private setBreadcrumb = (listName: string, recordName: string): void => {
    const listId: string = this.props.router.params.listId ?? '';
    const recordType: CustomListTabType = CustomListRouteUtil.resolveTab(this.props.router.params.recordType);
    const pages = CustomListRouteUtil.buildRecordBreadcrumb(
      listId,
      recordType,
      listName,
      recordName,
      this.props.router.location.pathname,
    );
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(pages.length - 1));
  };

  private getName = (record: CustomListRecordItem): string => {
    const names: CustomListWatchlistName[] = [];
    record.records?.forEach((entry: CustomListRecordEntry) => {
      entry.watchlistRecord?.names?.forEach((name) => names.push(name));
    });
    if (names.length === 0) {
      return LanguageUtil.get({id: 'common.label.nameUndefined', defaultMessage: 'Name Undefined'});
    }
    const primaryNames: CustomListWatchlistName[] = names.filter((name) => name.nameType === WatchlistNameType.PRIMARY);
    if (primaryNames.length === 0) {
      return names[0].value;
    }
    return primaryNames.reduce((left, right) => (left.value.length > right.value.length ? left : right)).value;
  };

  private getCategory = (record: CustomListRecordItem): string[] => {
    const categories: string[][] = [];
    record.records?.forEach((entry: CustomListRecordEntry) => {
      const watchlistRecord: CustomListWatchlistRecord | undefined = entry.watchlistRecord;
      if (watchlistRecord?.categories != undefined) {
        categories.push(watchlistRecord.categories);
      }
    });
    return _.uniqBy([].concat(...categories), (value: string) => value);
  };

  private getImage = (record: CustomListRecordItem, recordType: CustomListTabType): ReactNode => {
    if (recordType === 'individual') {
      const images = record.records?.filter(
        (entry) =>
          (entry.watchlistRecord as WatchlistIndividualRecord)?.images &&
          (entry.watchlistRecord as WatchlistIndividualRecord).images.length > 0,
      );
      if (images != undefined && images.length > 0) {
        const imageUrl: string = (images[0].watchlistRecord as WatchlistIndividualRecord).images[0];
        return (
          <img
            src={imageUrl}
            alt='-'
            onClick={() => window.open(imageUrl, '_blank')}
            className={
              'w-[60px] h-[60px] cursor-pointer border min-h-[60px] min-w-[60px] 2xl:w-[80px] 2xl:h-[80px] 2xl:min-h-[80px] 2xl:min-w-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE] object-cover'
            }
          />
        );
      }
      return <img src={'/icons/individual.svg'} alt='individual'/>;
    }
    return <img src={'/icons/entity.svg'} alt='entity'/>;
  };

  private mapToWatchlistModels = (records: CustomListRecordEntry[]): WatchlistModel[] => {
    return (records || []).map((entry: CustomListRecordEntry) => ({
      lastUpdated: entry.updatedAt,
      watchlistId: entry.watchlistId,
      watchlistRecord: entry.watchlistRecord as WatchlistModel['watchlistRecord'],
    }));
  };
}

export default withFormica(CustomListRecordDetailContainer);
